import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import ModalBase from "../../../../components/ui/ModalBase";
import colors from "../../../../shared/constants/color";
import axios from "axios";
import { SERVICEURL } from "../../../../appconfig";
import { useToastContext } from "../../../../context/ToastContextProvider";
import {
  getLanguageDetail,
  updateLanguageInfo,
} from "../../../../api/languageApi";

interface AddLanguageModalProps {
  open: boolean;
  memberId: string;
  languageId?: string | null;
  onClose: () => void;
}

export interface LanguageListDTO {
  languageID: string;
  memberID: string;
  languageName: string;
  speakingLevel: string;
  writingLevel: string;
  readingLevel: string;
  isEnough: boolean | null;
}

export interface AddLanguagePayload {
  MemberID: string;
  LanguageParameterID: string;
  SpeakingID?: string | null;
  WritingID?: string | null;
  ReadingID?: string | null;
  IsEnough?: boolean | null;
}

const initialFormState = {
  language: "",
  reading: "",
  writing: "",
  speaking: "",
};

const AddLanguageModal: React.FC<
  AddLanguageModalProps & { onSuccess?: (item: LanguageListDTO) => void }
> = ({
  open,
  memberId,
  languageId = null,
  onClose,
  onSuccess,
}) => {
    const isEditMode = !!languageId;
    const [form, setForm] = useState<typeof initialFormState>(initialFormState);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [languageOptions, setLanguageOptions] = useState<any[]>([]);
    const [levelOptions, setLevelOptions] = useState<any[]>([]);
    const { showToast } = useToastContext();
    const [confirmOpen, setConfirmOpen] = useState(false);

    const resetState = () => {
      setForm(initialFormState);
      setErrors({});
    };

    const handleClose = () => {
      resetState();
      onClose();
    };

    const handleChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      const { name, value } = e.target;
      setForm((prev) => ({ ...prev, [name]: value }));
    };

    useEffect(() => {
      if (!open) return;

      const fetchOptions = async () => {
        try {
          const token = localStorage.getItem("token");
          const headers = token
            ? {
              Authorization: `Bearer ${token}`,
            }
            : undefined;

          const [levelRes, languageRes] = await Promise.all([
            axios.get(`${SERVICEURL}api/Data/GetLevelList?`, { headers }),
            axios.get(`${SERVICEURL}api/CandidateParameters/GetLanguages`, {
              headers,
            }),
          ]);

          const levelData = levelRes.data?.data || [];
          const languageData = languageRes.data?.data || [];

          setLevelOptions(Array.isArray(levelData) ? levelData : []);
          setLanguageOptions(Array.isArray(languageData) ? languageData : []);
          
          // Düzenleme modundaysa veriyi yükle
          if (languageId) {
            await loadLanguageData(languageId);
          } else {
            resetState();
          }
        } catch (error) {
          console.error("Dil ve seviye listeleri yüklenirken hata oluştu:", error);
        }
      };

      fetchOptions();
    }, [open, languageId]);

    const loadLanguageData = async (id: string) => {
      try {
        const data = await getLanguageDetail(id);
        console.log("Loaded language data:", data);

        setForm({
          language: data.languageParameterId?.toString() || "",
          reading: data.readingId?.toString() || "",
          writing: data.writingId?.toString() || "",
          speaking: data.speakingId?.toString() || "",
        });

        console.log("Form set with:", {
          languageParameterId: data.languageParameterId,
          readingId: data.readingId,
          writingId: data.writingId,
          speakingId: data.speakingId,
        });
      } catch (error) {
        console.error("Error loading language data:", error);
        showToast("Dil bilgisi yüklenirken bir hata oluştu.", "error");
      }
    };

    const validate = () => {
      const newErrors: Record<string, string> = {};

      if (!form.language) {
        newErrors.language = "Yabancı dil seçimi zorunludur.";
      }
      if (!form.reading) {
        newErrors.reading = "Okuma bilgisi seçilmelidir";
      }
      if (!form.writing) {
        newErrors.writing = "Yazma bilgisi seçilmelidir";
      }
      if (!form.speaking) {
        newErrors.speaking = "Konuşma bilgisi seçilmelidir";
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleSaveClick = () => {
      if (!validate()) return;
      setConfirmOpen(true);
    };

    const handleSubmit = async () => {
      setConfirmOpen(false);

      try {
        setIsSubmitting(true);

        if (isEditMode && languageId) {
          // Düzenleme modu - PUT isteği
          const updatePayload = {
            languageId: languageId,
            languageParameterId: form.language,
            speakingId: form.speaking || undefined,
            writingId: form.writing || undefined,
            readingId: form.reading || undefined,
            isEnough: false,
          };

          console.log("Update language payload:", updatePayload);
          await updateLanguageInfo(updatePayload);
          showToast("Dil bilgisi başarıyla güncellendi.", "success");
          if (onSuccess) {
            onSuccess({} as LanguageListDTO);
          }
          handleClose();
        } else {
          // Ekleme modu - POST isteği
          const token = localStorage.getItem("token");

          const payload: AddLanguagePayload = {
            MemberID: memberId,
            LanguageParameterID: form.language,
            ReadingID: form.reading || null,
            WritingID: form.writing || null,
            SpeakingID: form.speaking || null,
            IsEnough: null,
          };

          const response = await axios.post(
            `${SERVICEURL}api/Candidates/AddLanguageInfo`,
            payload,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );

          const apiData = response.data?.data as LanguageListDTO | undefined;

          if (apiData && onSuccess) {
            onSuccess(apiData);
          }

          showToast("Yabancı dil bilgisi başarıyla eklendi.", "success");
          handleClose();
        }
      } catch (error) {
        console.error("Language save error:", error);
        const errorMessage = isEditMode
          ? "Dil bilgisi güncellenirken bir hata oluştu."
          : "Dil bilgisi eklenirken bir hata oluştu.";
        showToast(errorMessage, "error");
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <>
      <ModalBase
        open={open}
        onClose={handleClose}
        title={isEditMode ? "Yabancı Dil Düzenle" : "Yabancı Dil Ekle"}
        actions={
          <>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ borderRadius: 2, textTransform: "none", minWidth: 80 }}
            >
              Kapat
            </Button>
            <Button
              variant="contained"
              sx={{ borderRadius: 2, minWidth: 80 }}
              disabled={isSubmitting}
              onClick={handleSaveClick}
            >
              {isSubmitting ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </>
        }
        maxWidth="md"
        minWidth={700}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 2,
            width: "100%",
          }}
        >
          <TextField
            select
            fullWidth
            label="Yabancı Dil*"
            name="language"
            value={form.language}
            onChange={handleChange}
            size="small"
            error={!!errors.language}
            helperText={errors.language}
          >
            <MenuItem value="">Seçiniz</MenuItem>
            {languageOptions.map((lang: any) => (
              <MenuItem key={lang.id || lang.value || lang.name} value={lang.id}>
                {lang.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Okuma*"
            name="reading"
            value={form.reading}
            onChange={handleChange}
            size="small"
            error={!!errors.reading}
            helperText={errors.reading}
          >
            <MenuItem value="">Seçiniz</MenuItem>
            {levelOptions.map((level: any) => (
              <MenuItem
                key={level.id || level.value || level.name}
                value={level.id}
              >
                {level.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Yazma*"
            name="writing"
            value={form.writing}
            onChange={handleChange}
            size="small"
            error={!!errors.writing}
            helperText={errors.writing}
          >
            <MenuItem value="">Seçiniz</MenuItem>
            {levelOptions.map((level: any) => (
              <MenuItem
                key={level.id || level.value || level.name}
                value={level.id}
              >
                {level.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Konuşma*"
            name="speaking"
            value={form.speaking}
            onChange={handleChange}
            size="small"
            error={!!errors.speaking}
            helperText={errors.speaking}
          >
            <MenuItem value="">Seçiniz</MenuItem>
            {levelOptions.map((level: any) => (
              <MenuItem
                key={level.id || level.value || level.name}
                value={level.id}
              >
                {level.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </ModalBase>
      <Dialog
      open={confirmOpen}
      onClose={() => setConfirmOpen(false)}
      disableScrollLock
    >
        <DialogTitle>
          {isEditMode ? "Dil Bilgisi Güncellensin mi?" : "Dil Bilgisi Eklensin mi?"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {isEditMode 
              ? "Dil Bilgisi güncellemek istediğinize emin misiniz?" 
              : "Dil Bilgisi eklemek istediğinize emin misiniz?"}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: "none" }}>
            Hayır
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            sx={{ textTransform: "none", background: colors.blue.B800 }}
          >
            Evet
          </Button>
        </DialogActions>
      </Dialog>
      </>
    );
  };

export default AddLanguageModal;


